// Consent bootstrap: runs synchronously in <head>, owns consent state and Consent Mode ordering,
// loads Google tags and fetches the banner UI only when the visitor has to decide.
import { resolveConsentRegion } from './region.js';
import { STORAGE_VERSION, consentExpiry, createConsentStorage, parseRecord } from './storage.js';
import { choicesFromRecord, isBannerRequired, resolveEffectiveConsent } from './decision.js';
import { createGoogleIntegration, normalizeGoogleConfig } from './integrations/google.js';
import { CHANGE_EVENT, UI_READY_EVENT, injectScript, isObject, safeUrl, warn } from './utils.js';

const COOKIE_NAME = /^[A-Za-z0-9_-]{1,64}$/;
const COOKIE_DOMAIN = /^\.?[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*$/;
const POLICY_VERSION = /^[\w.:-]{1,64}$/;

export function createConsentBanner(win, { scriptSrc = null, nonce = '' } = {}) {
  let state = null;
  let uiPromise = null;

  const api = {
    /** Runs once; later calls are ignored so a second snippet cannot re-initialize consent. */
    init(options) {
      if (state) return api;
      const config = normalizeConfig(options, win, scriptSrc);
      const google = config.google && createGoogleIntegration(win, config.google, { nonce });
      const storage = createConsentStorage({
        doc: win.document,
        ...config.cookie,
        secure: win.location.protocol === 'https:',
      });

      // 1–2. Create/reuse the dataLayer(s) and queue Consent Mode default = GRANTED.
      if (google) google.initializeGoogleConsent();

      // 3. Resolve stored consent and region.
      state = {
        config,
        google,
        storage,
        region: resolveConsentRegion(config.geo, config.regimes),
        record: parseRecord(storage.read(), config),
        gpc: win.navigator.globalPrivacyControl === true,
        openedAt: 0,
      };

      // 4. Downgrade to DENIED where consent is required but missing — before any tag loads.
      if (google) google.updateGoogleConsent(effectiveConsent(), { initial: true });

      // 5. Load gtag.js / GTM. They stay loaded; Consent Mode decides what they may do.
      if (google) google.loadGoogleTags();

      // 6. Fetch the banner UI only when this visitor has to decide.
      if (isBannerRequired(state.region, state.record)) showUI('banner');
      return api;
    },

    /** Current consent snapshot, or null before init(). */
    getConsent() {
      return state ? snapshot() : null;
    },

    /** Opens the preference center, e.g. from a "Cookie settings" link. Loads the UI if needed. */
    openPreferences() {
      if (!state) {
        warn('openPreferences() called before init()');
        return Promise.resolve();
      }
      return showUI('preferences');
    },

    /** Forgets the stored decision and returns to the first-visit state. */
    reset() {
      if (!state) return;
      state.storage.clear();
      state.record = null;
      state.openedAt = 0;
      notifyChange();
      if (isBannerRequired(state.region, null)) showUI('banner');
      else if (uiPromise) uiPromise.then((ui) => ui.hide(), () => {});
    },
  };

  // The only surface the lazily loaded UI gets. It changes consent exclusively through save().
  const bridge = {
    get texts() {
      return state.config.texts;
    },
    get privacyPolicyUrl() {
      return state.config.privacyPolicyUrl;
    },
    getConsent: () => snapshot(),
    isDismissable: () => !isBannerRequired(state.region, state.record),
    save,
    markOpened,
  };

  return api;

  function effectiveConsent() {
    return resolveEffectiveConsent(choicesFromRecord(state.record), state.region, { gpc: state.gpc });
  }

  function snapshot() {
    const { region, record } = state;
    return {
      regime: region.regime,
      requireExplicitConsent: region.requireExplicitConsent,
      decided: Boolean(record),
      choices: choicesFromRecord(record),
      granted: effectiveConsent(),
      timestamps: record ? { ...record.timestamps } : null,
    };
  }

  /** Persists a decision made in the UI and propagates it to Google and the page. */
  function save(choices) {
    const now = Date.now();
    const previous = state.record;
    state.record = {
      version: STORAGE_VERSION,
      policyVersion: state.config.policyVersion,
      choices: { functional: true, analytics: choices.analytics === true, marketing: choices.marketing === true },
      timestamps: {
        createdAt: previous ? previous.timestamps.createdAt : now,
        updatedAt: now,
        bannerOpenedAt: state.openedAt || now,
      },
    };
    persist(now);
    notifyChange();
  }

  /** Opening the UI is not consent: without a stored decision the timestamp waits in memory. */
  function markOpened() {
    const now = Date.now();
    state.openedAt = now;
    if (!state.record) return;
    state.record = { ...state.record, timestamps: { ...state.record.timestamps, bannerOpenedAt: now } };
    persist(now);
  }

  function persist(now) {
    const expiresAt = consentExpiry(state.record.timestamps.updatedAt, state.config.lifetimeMonths);
    state.storage.write(state.record, Math.max(0, Math.round((expiresAt - now) / 1000)));
  }

  function notifyChange() {
    const consent = snapshot();
    if (state.google) state.google.updateGoogleConsent(consent.granted, { regime: consent.regime });
    win.dispatchEvent(new win.CustomEvent(CHANGE_EVENT, { detail: consent }));
  }

  function showUI(view) {
    return loadUI()
      .then((ui) => ui.show(view))
      .catch((error) => warn('Banner UI unavailable:', error));
  }

  /** Injects the UI script and stylesheet once. The UI bundle announces itself via UI_READY_EVENT. */
  function loadUI() {
    if (uiPromise) return uiPromise;
    const { script, style } = state.config.assets;
    if (!script) return Promise.reject(new Error('cannot resolve the banner script URL; set `assets.script`'));

    const scriptReady = new Promise((resolve, reject) => {
      const onReady = (event) => {
        const mount = event.detail && event.detail.mount;
        if (typeof mount !== 'function') return;
        win.removeEventListener(UI_READY_EVENT, onReady);
        resolve(mount);
      };
      win.addEventListener(UI_READY_EVENT, onReady);
      injectScript(win.document, script, {
        nonce,
        onError: () => {
          win.removeEventListener(UI_READY_EVENT, onReady);
          reject(new Error(`failed to load ${script}`));
        },
      });
    });
    const styleReady = style ? loadStyle(win.document, style, nonce) : null;

    uiPromise = Promise.all([scriptReady, styleReady]).then(([mount]) => mount(win, bridge));
    uiPromise.catch(() => {
      uiPromise = null; // allow a retry, e.g. from a later openPreferences()
    });
    return uiPromise;
  }
}

function loadStyle(doc, href, nonce) {
  return new Promise((resolve) => {
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    if (nonce) link.setAttribute('nonce', nonce);
    // A stylesheet that fails to load must not keep the visitor from deciding.
    link.addEventListener('load', resolve);
    link.addEventListener('error', resolve);
    // First in <head>: host stylesheets come later in the cascade and win ties.
    const head = doc.head || doc.documentElement;
    head.insertBefore(link, head.firstChild);
  });
}

function normalizeConfig(options, win, scriptSrc) {
  const input = isObject(options) ? options : {};
  const cookie = isObject(input.cookie) ? input.cookie : {};
  const assets = isObject(input.assets) ? input.assets : {};
  const base = win.document.baseURI;
  const policyVersion = typeof input.policyVersion === 'number' ? String(input.policyVersion) : input.policyVersion;
  const months = input.consentLifetimeMonths;
  // UI assets default to files next to consent-bootstrap.js.
  const sibling = (file) => (scriptSrc ? safeUrl(file, scriptSrc) : null);

  return {
    geo: input.geo,
    regimes: input.regimes,
    policyVersion: matches(POLICY_VERSION, policyVersion) ? policyVersion : '1',
    lifetimeMonths: Number.isInteger(months) && months > 0 ? months : 14,
    cookie: {
      name: matches(COOKIE_NAME, cookie.name) ? cookie.name : 'consent_banner',
      domain: matches(COOKIE_DOMAIN, cookie.domain) ? cookie.domain : null,
    },
    privacyPolicyUrl: safeUrl(input.privacyPolicyUrl, base),
    texts: isObject(input.texts) ? input.texts : {},
    google: normalizeGoogleConfig(input.google, base),
    assets: {
      script: safeUrl(assets.script, base) || sibling('consent-banner.js'),
      style: assets.style === false ? null : safeUrl(assets.style, base) || sibling('consent-banner.css'),
    },
  };
}

// RegExp#test(undefined) would test the string "undefined".
function matches(pattern, value) {
  return typeof value === 'string' && pattern.test(value);
}

// Browser entry: install one shared instance. A second copy of this script keeps the first.
if (typeof window !== 'undefined' && !window.ConsentBanner) {
  const script = document.currentScript;
  window.ConsentBanner = createConsentBanner(window, {
    scriptSrc: (script && script.src) || null,
    nonce: (script && script.nonce) || '',
  });
}
